import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import * as mapboxgl from 'mapbox-gl';
import { Feature, FeatureCollection, Geometry, LineString } from 'geojson';
import { calculateTotalDistance } from './distance';

interface DateRange {
  gte: string;
  lte: string;
}

interface NumberRange {
  gte: number;
  lte: number;
}

interface FilterQuery {
  query: Array<{
    fish_type?: string[];
    date?: DateRange;
    temperature?: NumberRange;
    wind_speed?: NumberRange;
    wind_direction?: string;
    tide?: string[];
    trip_id?: string;
    conditions?: string[];
  }>;
}

interface FishingTrip {
  trip_id: string;
  start_time: Date;
}

interface FishType {
  fish_name: string;
  fish_full_name: string;
  fish_display_color: string;
  icon: string | null;
}

interface WeatherCondition {
  short_forecast: string;
  icon: string | null;
}

@Component({
  selector: 'app-tracks',
  templateUrl: './maps.component.html',
  styleUrls: ['./maps.component.scss'],
  imports: [CommonModule, ReactiveFormsModule],
  standalone: true
})
export class TracksComponent implements OnInit {
  map!: mapboxgl.Map;
  popup!: mapboxgl.Popup;

  // Map configuration
  style = 'mapbox://styles/mapbox/standard-satellite';
  lat = 39.001904;
  lng = -76.430749;
  filterForm: FormGroup;

  allFishingTrips: FishingTrip[] = [];
  windDirections = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  tideStates: string[] = [];
  weatherConditions: WeatherCondition[] = [];
  fishTypes: FishType[] = [];
  heatMap = true;
  coordinates: LineString = {
    type: 'LineString',
    coordinates: []
  };

  constructor(
    private http: HttpClient,
    private fb: FormBuilder
  ) {
    this.filterForm = this.fb.group({
      selectedFishTypes: [[]],
      dateRange: this.fb.group({
        start: [new Date(new Date().setFullYear(new Date().getFullYear() - 2)).toISOString().split('T')[0]],
        end: [new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0]]
      }),
      temperature: this.fb.group({
        min: ['40'],
        max: ['80']
      }),
      windSpeed: this.fb.group({
        min: ['0'],
        max: ['20']
      }),
      windDirection: [''],
      tide: [[]],
      trip_id: [''],
      conditions: [[]]
    });
  }

  ngOnInit(): void {
    this.initializeMap();
    this.getFishingTrips();
    this.loadFormOptions();
    // Subscribe to form changes
    this.filterForm.valueChanges.subscribe(() => {
      this.applyFilters();
    });
  }

  private loadFormOptions(): void {
    this.http.get<FishType[]>(`${environment.apiUrl}/fish_types`).subscribe(data => {
      this.fishTypes = data;
      const defaultSelected = data
        .map(f => f.fish_name)
        .filter(name => name !== 'location' && name !== 'no_fish');
      this.filterForm.patchValue({ selectedFishTypes: defaultSelected }, { emitEvent: false });
      if (this.map.loaded()) {
        this.applyFilters();
      }
    });
    this.http.get<WeatherCondition[]>(`${environment.apiUrl}/weather_types`).subscribe(data => {
      this.weatherConditions = data;
    });
    this.http.get<string[]>(`${environment.apiUrl}/tide_types`).subscribe(data => {
      this.tideStates = data;
    });
  }

  private initializeMap(): void {
    this.map = new mapboxgl.Map({
      accessToken: environment.mapbox.accessToken,
      container: 'map',
      style: this.style,
      center: [this.lng, this.lat],
      zoom: 13,
      dragRotate: false,
      touchPitch: false
    });

    // Create popup for later use
    this.popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false
    });

    this.map.on('load', () => {
      this.map.addSource('fish-data', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      this.map.addSource('route', {
        type: 'geojson',
        lineMetrics: true,
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [],
          }
        }
      });

      this.addFishLayers();
      this.addPopupInteraction();
      this.applyFilters();
    });
  }

  private getFishingTrips(): void {
    this.http.post<FishingTrip[]>(`${environment.apiUrl}/fishing_trip`, {action: 'get_all'}).subscribe((data) => {
      this.allFishingTrips = data;
    });
  }

  private loadInitialData(): void {
    this.http.get<FeatureCollection>(`${environment.apiUrl}/catches`).subscribe((data) => {
      (this.map.getSource('fish-data') as mapboxgl.GeoJSONSource).setData(data);
    });
  }

  private buildFilterQuery(): FilterQuery {
    const formValue = this.filterForm.value;
    const query: FilterQuery = { query: [] };

    if (formValue.selectedFishTypes?.length) {
      query.query.push({ fish_type: formValue.selectedFishTypes });
    }

    if (formValue.dateRange.start || formValue.dateRange.end) {
      query.query.push({
        date: {
          gte: new Date(formValue.dateRange.start).toISOString(),
          lte: new Date(formValue.dateRange.end).toISOString()
        }
      });
    }

    if (formValue.temperature.min || formValue.temperature.max) {
      query.query.push({
        temperature: {
          gte: Number(formValue.temperature.min) || 0,
          lte: Number(formValue.temperature.max) || 100
        }
      });
    }

    if (formValue.windSpeed.min || formValue.windSpeed.max) {
      query.query.push({
        wind_speed: {
          gte: Number(formValue.windSpeed.min) || 0,
          lte: Number(formValue.windSpeed.max) || 100
        }
      });
    }

    if (formValue.windDirection) {
      query.query.push({ wind_direction: formValue.windDirection });
    }

    if (formValue.tide?.length) {
      query.query.push({ tide: formValue.tide });
    }

    if (formValue.trip_id) {
      query.query.push({ trip_id: formValue.trip_id.trip_id });
    }

    if (formValue.conditions?.length) {
      query.query.push({ conditions: formValue.conditions });
    }

    return query;
  }

  applyFilters(): void {
    const query = this.buildFilterQuery();

    if (query.query.length === 0) {
      this.loadInitialData();
      return;
    }

    this.http.post<FeatureCollection>(`${environment.apiUrl}/catches`, query).subscribe((data) => {
      (this.map.getSource('fish-data') as mapboxgl.GeoJSONSource).setData(data);
    });

    this.http.post<LineString>(`${environment.apiUrl}/tracks`, query).subscribe((data) => {
      (this.map.getSource('route') as mapboxgl.GeoJSONSource).setData({
        type: 'LineString',
        coordinates: data.coordinates,
      });
      calculateTotalDistance(data);
    });
  }

  addFishLayers(): void {
    this.map.addLayer({
      id: 'fishes',
      type: 'circle',
      source: 'fish-data',
      paint: {
        'circle-radius': 5,
        'circle-color': ['get', 'fish_type_display_color'],
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff',
      },
    });

    this.map.addLayer({
      id: 'heatMap',
      type: 'heatmap',
      source: 'fish-data',
      filter: ['!=', 'fish_type', 'no_fish'],
      paint: {
        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0,
          'rgba(33,102,172,0)',
          0.5,
          'rgb(103,169,207)',
          0.9,
          'rgb(239,138,98)',
          1,
          'rgb(178,24,43)'
        ],
        'heatmap-opacity': 0.8,
      },
    });

    this.map.addLayer({
      id: 'route',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': 'red',
        'line-width': 2,
        'line-opacity': 0.7,
        'line-gradient': [
          'interpolate',
          ['linear'],
          ['line-progress'],
          0,
          'blue',
          0.1,
          'royalblue',
          0.3,
          'cyan',
          0.5,
          'lime',
          0.7,
          'yellow',
          1,
          'red'
        ]
      }
    });
  }

  private addPopupInteraction(): void {
    this.map.on('mousemove', 'fishes', (e) => {
      this.map.getCanvas().style.cursor = 'pointer';

      if (e.features && e.features.length > 0) {
        const feature = e.features[0];

        if (feature.geometry.type === 'Point' && 'coordinates' in feature.geometry) {
          const pointGeometry = feature.geometry as GeoJSON.Point;
          const popupContent = `
            <div>
            <strong>Fish Type:</strong> ${feature.properties?.['fish_type_full_name'] ?? 'Unknown'}<br>
            <strong>Temperature:</strong> ${feature.properties?.['temperature'] ?? 'N/A'}°F<br>
            <strong>Water Temp:</strong> ${feature.properties?.['water_temperature'] ?? 'N/A'}°F ${feature.properties?.['water_temperature_trend'] ?? ''}<br>
            <strong>Tide:</strong> ${feature.properties?.['tide_cycle'] ?? 'Unknown'}<br>
            <strong>Wind:</strong> ${feature.properties?.['wind_speed'] ?? 'N/A'} ${feature.properties?.['wind_direction'] ?? ''}<br>
            <strong>Forecast:</strong> ${feature.properties?.['short_forecast'] ?? 'No forecast'}<br>
            <strong>Time:</strong> ${feature.properties?.['time'] ? new Date(feature.properties['time']).toLocaleString() : 'Unknown'}
            </div>
          `;

          this.popup
            .setLngLat(pointGeometry.coordinates as [number, number])
            .setHTML(popupContent)
            .addTo(this.map);
        }
      }
    });

    this.map.on('mouseleave', 'fishes', () => {
      this.map.getCanvas().style.cursor = '';
      this.popup.remove();
    });
  }

  toggleLayer(layerName: string, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked !== undefined) {
      this.map.setLayoutProperty(layerName, 'visibility', checkbox.checked ? 'visible' : 'none');
    }
  }

  updateConditions(condition: string, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const conditions = this.filterForm.get('conditions')?.value || [];

    if (checkbox.checked) {
      conditions.push(condition);
    } else {
      const index = conditions.indexOf(condition);
      if (index > -1) {
        conditions.splice(index, 1);
      }
    }

    this.filterForm.patchValue({ conditions });
  }

  updateFishTypes(fishName: string, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const selectedFishTypes = this.filterForm.get('selectedFishTypes')?.value || [];

    if (checkbox.checked) {
      selectedFishTypes.push(fishName);
    } else {
      const index = selectedFishTypes.indexOf(fishName);
      if (index > -1) {
        selectedFishTypes.splice(index, 1);
      }
    }

    this.filterForm.patchValue({ selectedFishTypes });
  }

  updateTides(tideState: string, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const tide = this.filterForm.get('tide')?.value || [];

    if (checkbox.checked) {
      tide.push(tideState);
    } else {
      const index = tide.indexOf(tideState);
      if (index > -1) {
        tide.splice(index, 1);
      }
    }

    this.filterForm.patchValue({ tide });
  }
}
